import { Controller } from "@hotwired/stimulus"

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Shared by the admin Task and Reminder forms. Drives the "Every N Week/Month/Year"
// recurrence control (unit groups, interval options, yearly day resize) and the
// default-checkbox dimming of the practice-assignment region.
export default class extends Controller {
  static targets = ["interval", "weekGroup", "monthGroup", "yearGroup", "yearlyMonth", "yearlyDayOfMonth", "startMonthGroup", "startMonth", "startMonthHint", "practiceAssignment"]
  static values = { maxIntervals: Object }

  connect() {
    this.updateUnit()
    this.updateYearlyDays()
    this.updateDefault()
  }

  updateUnit() {
    const unit = this.unitSelect?.value || "week"
    const groups = [["week", this.weekGroupTarget], ["month", this.monthGroupTarget], ["year", this.yearGroupTarget]]

    groups.forEach(([u, el]) => {
      const active = u === unit
      el.classList.toggle("hidden", !active)
      // Disable hidden groups' inputs so only the active unit's fields post
      // (also removes the duplicate day_of_month between the month/year groups).
      el.querySelectorAll("select, input").forEach(i => { i.disabled = !active })
    })

    this.updateIntervalOptions(unit)
    // After the group loop, which re-enables every input in the active group.
    this.updateStartMonth()
  }

  // The monthly "Starting on" anchor only applies to "every N months" with N >= 2 —
  // at N = 1 every month is an occurrence, so there is nothing to anchor. The hint
  // mirrors Scheduling::Recurrence#ice_cube_rule: step from the start month to December.
  updateStartMonth() {
    if (!this.hasStartMonthGroupTarget || !this.hasIntervalTarget) return

    const unit = this.unitSelect?.value || "week"
    const interval = parseInt(this.intervalTarget.value, 10) || 1
    const active = unit === "month" && interval >= 2

    this.startMonthGroupTarget.classList.toggle("hidden", !active)
    if (!active) return

    const start = parseInt(this.startMonthTarget.value, 10) || 1
    const months = []
    for (let m = start; m <= 12; m += interval) months.push(MONTH_ABBR[m - 1])
    this.startMonthHintTarget.textContent = `Occurs: ${months.join(", ")}`
  }

  // Resize the interval select to the unit's cap, preserving the selection when
  // still valid and clamping down otherwise.
  updateIntervalOptions(unit) {
    if (!this.hasIntervalTarget) return

    const max = this.maxIntervalsValue[unit] || 1
    const select = this.intervalTarget
    const desired = Math.min(parseInt(select.value, 10) || 1, max)

    select.innerHTML = ""
    for (let n = 1; n <= max; n++) {
      const option = document.createElement("option")
      option.value = n
      option.textContent = n
      option.selected = n === desired
      select.appendChild(option)
    }
  }

  // Resize the yearly day options to the chosen month (Feb capped at 28 to stay
  // leap-year-agnostic), preserving the selected day when it is still valid.
  updateYearlyDays() {
    if (!this.hasYearlyMonthTarget || !this.hasYearlyDayOfMonthTarget) return

    const month = parseInt(this.yearlyMonthTarget.value, 10)
    const max = DAYS_IN_MONTH[month - 1] || 31
    const select = this.yearlyDayOfMonthTarget
    const desired = Math.min(parseInt(select.value, 10) || 1, max)

    select.innerHTML = ""
    for (let d = 1; d <= max; d++) {
      const option = document.createElement("option")
      option.value = d
      option.textContent = this.ordinalize(d)
      option.selected = d === desired
      select.appendChild(option)
    }
  }

  ordinalize(n) {
    const suffixes = ["th", "st", "nd", "rd"]
    const v = n % 100
    return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`
  }

  updateDefault() {
    const isDefault = this.element.querySelector("input[type='checkbox'][name$='[default]']")?.checked
    const region = this.practiceAssignmentTarget
    region.classList.toggle("opacity-50", isDefault)
    region.classList.toggle("pointer-events-none", isDefault)
    region.querySelectorAll("input, button").forEach(el => { el.disabled = isDefault })
  }

  get unitSelect() {
    return this.element.querySelector("[name$='[recurrence][unit]']")
  }
}
